import { createTheme, ThemeProvider, Tooltip } from "@mui/material";
import { Component, createRef, RefObject } from "react";
import TournamentInformation from "./TournamentInformation";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import styles from '../../../../assets/css/components/Lobby.module.css';
import { ITournamentState } from "../../../../Core/State/ITournamentState";
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import playersIcon from '/images/lobby/players.svg';
import freerollIcon from '/images/lobby/freeroll.svg';
import qualifierIcon from '/images/lobby/qualifier.svg';
import discordIcon from '/images/lobby/discord.svg';
import facebookIcon from '/images/lobby/facebook.svg';
import instagramIcon from '/images/lobby/instagram.svg';
import metamaskIcon from '/images/lobby/metamask.svg';
import oplIcon from '/images/lobby/opl.svg';
import redditIcon from '/images/lobby/reddit.svg';
import telegramIcon from '/images/lobby/telegram.svg';
import xIcon from '/images/lobby/x.svg';
import kickIcon from '/images/lobby/kick.svg';
import twitchIcon from '/images/lobby/twitch.svg';

const getIcon = (brand: string): string => {
    const brandMap: Record<string, string> = {
        "discord": discordIcon,
        "facebook": facebookIcon,
        "instagram": instagramIcon,
        "metamask": metamaskIcon,
        "opl": oplIcon,
        "reddit": redditIcon,
        "telegram": telegramIcon,
        "x": xIcon,
        "kick": kickIcon,
        "twitch": twitchIcon
    }
    return brandMap[brand];
};

interface MttLobbyState {
    isDragging: boolean;
    lastClientY: number;
}

export default class DesktopMttLobby extends Component<{}, MttLobbyState> {
    private dataGridRef: RefObject<HTMLDivElement>;
    private lastClientY: number = 0;
    
    constructor(props: {}) {
        super(props);
        this.state = {
            isDragging: false,
            lastClientY: 0
        };
        this.dataGridRef = createRef<HTMLDivElement>();
    }

    darkTheme = createTheme({
        palette: {
            mode: "dark",
            primary: {
                main: "#F93751",
            },
        },
        typography: {
            fontFamily: 'Inter, serif',
        },
    });

    handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        // Prevent default to avoid text selection during drag
        e.preventDefault();
        this.lastClientY = e.clientY;
        this.setState({ isDragging: true });
    };

    handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (this.state.isDragging && this.dataGridRef.current) {
            const delta = this.lastClientY - e.clientY;
            
            // Find the virtual scroller element inside the DataGrid
            const virtualScroller = this.dataGridRef.current.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement;
            if (virtualScroller) {
                virtualScroller.scrollTop += delta;
                this.lastClientY = e.clientY;
            }
        }
    };

    handleMouseUp = () => {
        this.setState({ isDragging: false });
    };

    handleMouseLeave = () => {
        this.setState({ isDragging: false });
    };

    render() {
        const tournaments: ITournamentState[] = [
            {tournamentId: 1, start: new Date(Date.now() - 1165000), name: "Monthly 25 Qualifier", gameType: "Hold'em", buyIn: 0.25, fee: 0.05, players: 0, state: "In Play", prize: 500, promo: ["discord", "x", "metamask"]},
            {tournamentId: 2, start: new Date(Date.now() + 6305000), name: "Merch Pack Giveaway (1 GTD)", gameType: "Hold'em", buyIn: 0, fee: 0, players: 5, state: "Registering", prize: 0, promo: ["metamask", "instagram", "x", "kick"]},
            {tournamentId: 3, start: new Date(Date.now() + 8105000), name: "Merch Pack Giveaway (2 GTD)", gameType: "Hold'em", buyIn: 0.50, fee: 0.10, players: 3, state: "Registering", prize: 1000, promo: ["x", "facebook"]},
            {tournamentId: 4, start: new Date(Date.now() + 11105000), name: "Thursday 10 $500 GTD", gameType: "Hold'em", buyIn: 10, fee: 1, players: 0, state: "Registering", prize: 1000, promo: []},
            {tournamentId: 5, start: new Date(Date.now() + 16105000), name: "Monthly 25 Qualifier", gameType: "Hold'em", buyIn: 0.50, fee: 0.10, players: 1, state: "Registering", prize: 500, satellite: true, promo: ["twitch", "kick", "x", "telegram"]},
            {tournamentId: 6, start: new Date(Date.now() - 3465000), name: "Monthly 25 Qualifier", gameType: "Hold'em", buyIn: 0, fee: 0, players: 2, state: "Late Registration", prize: 0, promo: ["instagram", "facebook"]},
            {tournamentId: 7, start: new Date(Date.now() - 5965000), name: "Monthly 25 Qualifier", gameType: "Hold'em", buyIn: 0.25, fee: 0.05, players: 185, state: "Late Registration", prize: 500, satellite: true, promo: []},
            {tournamentId: 8, start: new Date(Date.now() - 7965000), name: "Monthly 25 Qualifier", gameType: "Hold'em", buyIn: 0, fee: 0, players: 3, state: "Late Registration", prize: 0, promo: ["reddit", "discord", "metamask"]},
            {tournamentId: 9, start: new Date(Date.now() - 10265000), name: "Monthly 25 Qualifier", gameType: "Hold'em", buyIn: 0, fee: 0, players: 6, state: "Late Registration", prize: 0, promo: ["opl", "x", "instagram"]},
            {tournamentId: 10, start: new Date(Date.now() - 13565000), name: "Monthly 25 Qualifier", gameType: "Hold'em", buyIn: 0, fee: 0, players: 0, state: "Late Registration", prize: 0, promo: []},
            {tournamentId: 11, start: new Date(Date.now() - 16565000), name: "Monthly 25 Qualifier", gameType: "Hold'em", buyIn: 0, fee: 0, players: 2, state: "Completed", prize: 0, promo: []},
            {tournamentId: 12, start: new Date(Date.now() - 19145000), name: "Monthly 25 Qualifier", gameType: "Hold'em", buyIn: 10, fee: 1, players: 5, state: "Completed", prize: 1000, promo: ["facebook", "x", "twitch"]},
            {tournamentId: 13, start: new Date(Date.now() - 25465000), name: "Monthly 25 Qualifier", gameType: "Hold'em", buyIn: 0.25, fee: 0.05, players: 5, state: "Completed", prize: 500, promo: ["kick", "x", "metamask", "telegram"]},
            {tournamentId: 14, start: new Date(Date.now() - 32115000), name: "Monthly 25 Qualifier", gameType: "Hold'em", buyIn: 0.50, fee: 0.10, players: 10, state: "Completed", prize: 20, promo: []},
            {tournamentId: 15, start: new Date(Date.now() - 39225000), name: "Monthly 25 Qualifier", gameType: "Hold'em", buyIn: 0, fee: 0, players: 0, state: "Cancelled", prize: 0, promo: []},
            {tournamentId: 16, start: new Date(Date.now() - 42565000), name: "Monthly 25 Qualifier", gameType: "Hold'em", buyIn: 0, fee: 0, players: 0, state: "Cancelled", prize: 0, promo: ["discord", "instagram", "facebook", "x"]},
            {tournamentId: 17, start: new Date(Date.now() - 68525000), name: "Monthly 25 Qualifier", gameType: "Hold'em", buyIn: 0.50, fee: 0.10, players: 0, state: "Cancelled", prize: 0, promo: []}
        ];
        const columns: GridColDef<ITournamentState>[] = [
            { field: 'start', minWidth: 160, headerName: 'Start', headerClassName: styles.tableHeader, type: 'dateTime', resizable: false, valueFormatter: (params) => {
                const date = params.value as Date;
                return `${date.getDate()} ${date.toLocaleString('en-US', { month: 'short' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })}`;
            } },
            { field: 'name', minWidth: 200, flex: 1, headerName: 'Name', headerClassName: styles.tableHeader, type: 'string', resizable: false, renderCell: (params) => {
                const { buyIn, satellite, promo, name } = params.row;

                return (
                  <div style={{ display: 'flex', width: '100%', paddingRight: '20px', alignItems: 'center' }}>
                    {/* Name text */}
                    <span style={{ marginRight: 'auto' }}>{name}</span>
          
                    {/* Conditionally add the icons */}
                    {buyIn === 0 && (
                        <Tooltip title="Freeroll - This tournament has no entry fee" placement="top" arrow>
                            <img src={freerollIcon} alt="Freeroll Icon" style={{ marginLeft: 5 }} />
                        </Tooltip>
                    )}
                    {satellite && (
                        <Tooltip title="Satellite - The prize of this tournament is entry into a larger tournament" placement="top" arrow>
                            <img src={qualifierIcon} alt="Qualifier Icon" style={{ marginLeft: 5 }} />
                        </Tooltip>
                    )}
                    {promo.map((item) => (
                        <Tooltip title="This tournament is organized by: Someone" placement="top" arrow>
                            <img src={getIcon(item)} alt="Reddit Icon" height={22} style={{ marginLeft: 5 }} />
                        </Tooltip>
                    ))}
                  </div>
                );
            } },
            { field: 'gameType', minWidth: 120, headerName: 'Game Type', headerClassName: styles.tableHeader, type: 'string', resizable: false },
            { field: 'buyIn', minWidth: 150, headerName: 'Buy-in', headerClassName: styles.tableHeader, type: 'string', resizable: false, valueGetter: (params) => {
                const { buyIn, fee } = params.row;
                const formatNumber = (num: number) => Number.isInteger(num) ? num.toString() : num.toFixed(2);
                if (buyIn === 0) {
                  return "Freeroll";
                }
                return `$${formatNumber(buyIn)} + $${formatNumber(fee)}`;
            } },
            { field: 'players', headerName: 'Players', headerClassName: styles.tableHeader, type: 'number', resizable: false, align: 'left', headerAlign: 'left', renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img src={playersIcon} alt="Players Icon" style={{ width: 20, height: 20, marginRight: 8 }} />
                    {params.value}
                </div>
            ) },
            { field: 'state', minWidth: 150, headerName: 'State', headerClassName: styles.tableHeader, type: 'string', resizable: false, renderCell: (params) => {
                const stateColors: Record<string, string> = {
                    'In Play': '#6BD2FF',
                    'Registering': '#1BE958',
                    'Late Registration': '#EFC216',
                    'Completed': '#16EFC1',
                    'Cancelled': '#B3B3B3',
                };
            
                  return (
                    <span
                      style={{
                        color: stateColors[params.value] || '#000',
                        fontWeight: '500',
                        fontSize: '12px',
                        backgroundColor: `${stateColors[params.value]}33`,
                        borderRadius: '3px',
                        padding: '2px 4px'
                      }}
                    >
                      {params.value}
                    </span>
                  );
             } },
            { field: 'prize', headerName: 'Prize', headerClassName: styles.tableHeader, cellClassName: styles.lastTableCell, type: 'string', resizable: false, valueFormatter: (params) => {
                const prize = params.value as number;
                return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(prize);
            }, renderCell: (params) => {
                return (
                    <span
                      style={{
                        fontSize: '14px',
                        color: '#FFC100',
                        fontWeight: 'bold',
                      }}
                    >
                      {params.formattedValue} {/* Use the formatted value */}
                    </span>
                  );
            } },
        ];

        const customDataGridClasses = {
            virtualScroller: styles.customScroller,
            columnHeaders: styles.tableHeaderRow,
            columnHeader: styles.tableHeaderCell,
            cell: styles.tableCell
        }
        return (
            <ThemeProvider theme={this.darkTheme}>
                <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: 'calc(100dvh - 130px)', width: '100%', marginTop: '20px' }}>
                    <div className={styles.overlay}>
                        <span style={{ fontSize: '32px', fontWeight: 600, fontFamily: '"Inter", serif' }}>Coming Soon</span>
                    </div>
                    <div style={{ display: 'flex', flexGrow: 1, height: '100%' }}>
                        <div 
                            style={{ flex: '0 0 calc(100% - 360px)', marginRight: '20px', backgroundColor: '#2a2b32' }}
                            ref={this.dataGridRef}
                            onMouseDown={this.handleMouseDown}
                            onMouseMove={this.handleMouseMove}
                            onMouseUp={this.handleMouseUp}
                            onMouseLeave={this.handleMouseLeave}
                        >
                            <DataGrid
                                disableColumnMenu
                                disableColumnFilter
                                getCellClassName={(params) => styles.tableCell}
                                getRowClassName={(params) => styles.tableRow}
                                rows={tournaments}
                                columns={columns}
                                classes={customDataGridClasses}
                                pageSizeOptions={[500]}
                                getRowId={(row) => row.tournamentId}
                                hideFooter
                                // onRowClick={this.handleRowClick}
                                slots={{
                                    columnSortedDescendingIcon: ArrowDropUp,
                                    columnSortedAscendingIcon: ArrowDropDown,
                                }}
                                sx={{
                                    border: 'none',
                                    color: 'white',
                                    height: '100%',
                                    cursor: this.state.isDragging ? 'grabbing' : 'grab',
                                    '& .MuiDataGrid-virtualScroller': {
                                        '&::-webkit-scrollbar': {
                                            width: '0.4em',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: 'transparent',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            backgroundColor: 'rgba(255,255,255,.3)',
                                        },
                                        '&::-webkit-scrollbar-thumb:hover': {
                                            background: 'rgba(255,255,255,.5)',
                                        },
                                    },
                                    '& .MuiDataGrid-root': {
                                        fontFamily: 'Inter, serif'
                                    },
                                    '& .MuiDataGrid-columnHeader': {
                                        fontFamily: 'Inter, serif'
                                    },
                                    '& .MuiDataGrid-cell': {
                                        fontFamily: 'Inter, serif'
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        cursor: 'pointer',
                                        backgroundColor: '#3A3D50 !important',
                                    },
                                    '& .MuiDataGrid-row:focus': {
                                        backgroundColor: '#3A3D50',
                                    },
                                    '& .MuiDataGrid-row:active': {
                                        backgroundColor: '#3A3D50',
                                    },
                                    '& .MuiDataGrid-sortIcon': {
                                        color: '#66666b',
                                        fontSize: '2rem'
                                    },
                                    '& .MuiDataGrid-row.Mui-selected': {
                                        backgroundColor: '#3A3D50',
                                    },
                                    '& .MuiDataGrid-columnSeparator:not(.MuiDataGrid-columnSeparator--resizable)': {
                                        display: 'none !important',
                                    },
                                }}
                            />
                        </div>
                        <div style={{ flex: '0 0 340px', display: 'flex', flexDirection: 'column', height: '100%', color: 'white' }}>
                            <div style={{ height: '100%' }}>
                                <TournamentInformation />
                            </div>
                        </div>
                    </div>
                </div>
            </ThemeProvider>
        );
    }
}
